// ============================================================
// MODULES (plan armoury, stage 5) - what a forge puts on a weapon.
//
// A module is the GM's own: a name, free tags, an optional SLOT (two
// modules in one slot clash; one with no slot stacks with anything), a FIT
// (a condition over the weapon's facts, in the one condition language:
// "the weapon is tagged bolt", "at range") and its CHANGES - a closed list:
//
//   - a number set, added to or multiplied: Pen, range, clip, semi, full,
//     to hit, reach, damage dice, flat damage;
//   - a special gained or lost;
//   - the Signature set to a step;
//
// and, optionally, conditional rules it brings (`weaponRules`).
// Craftsmanship is four built-in modules in their own slot.
//
// Pure. Zero React dependency.
// ============================================================
import { readWhen, type When } from '../character/effectWhen';
import { SIGNATURES, type Signature } from '../stealth/noise';
import { readWeaponRules, type WeaponRule } from '../weaponRules';

export const NUMBER_FIELDS = [
  'penetration',
  'range',
  'clip',
  'semi',
  'full',
  'attackMod',
  'reach',
  'damageDice',
  'damageFlat',
] as const;
export type NumberField = (typeof NUMBER_FIELDS)[number];
const OPS = ['set', 'add', 'multiply'] as const;

/** A change, and - optionally - the class of weapon it is for (craftsmanship differs for a gun and a blade). */
export type ModuleChange = (
  | { field: NumberField; op: (typeof OPS)[number]; value: number }
  | { special: string; op: 'gain' | 'lose' }
  | { signature: Signature }
) & { for?: 'melee' | 'ranged' };

export interface Module {
  name: string;
  tags?: string[];
  /** Two modules in one slot clash; absent, it stacks with anything. */
  slot?: string;
  /** What it fits: a condition over the weapon's facts. Absent, anything. */
  fits?: When;
  changes: ModuleChange[];
  rules?: WeaponRule[];
}

type Raw = Record<string, unknown>;
const isRecord = (v: unknown): v is Raw => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max = 40) => (typeof v === 'string' ? v.trim().slice(0, max) : '');

function readWhat(raw: Raw): ModuleChange | null {
  if ((NUMBER_FIELDS as readonly unknown[]).includes(raw.field) && (OPS as readonly unknown[]).includes(raw.op)) {
    const value = Number(raw.value);
    return Number.isFinite(value)
      ? { field: raw.field as NumberField, op: raw.op as (typeof OPS)[number], value }
      : null;
  }
  if (text(raw.special) && (raw.op === 'gain' || raw.op === 'lose')) return { special: text(raw.special), op: raw.op };
  return SIGNATURES.includes(raw.signature as Signature) ? { signature: raw.signature as Signature } : null;
}

function readChange(raw: unknown): ModuleChange | null {
  if (!isRecord(raw)) return null;
  const what = readWhat(raw);
  if (!what) return null;
  return raw.for === 'melee' || raw.for === 'ranged' ? { ...what, for: raw.for } : what;
}

/** A module's optional parts off storage, each only when it reads. */
function extrasOf(raw: Raw): Pick<Module, 'tags' | 'slot' | 'fits'> {
  const fits = readWhen(raw.fits);
  const tags = Array.isArray(raw.tags) ? raw.tags.map((t) => text(t)).filter(Boolean) : [];
  const slot = text(raw.slot);
  return { ...(tags.length ? { tags } : {}), ...(slot ? { slot } : {}), ...(fits ? { fits } : {}) };
}

/** A module off storage; null when it has no name or nothing it can change. */
export function readModule(raw: unknown): Module | null {
  if (!isRecord(raw)) return null;
  const changes = Array.isArray(raw.changes) ? raw.changes.map(readChange).filter((c) => c !== null) : [];
  const rules = readWeaponRules(raw.rules);
  const name = text(raw.name, 60);
  if (!name || (!changes.length && !rules.length)) return null;
  return { name, ...extrasOf(raw), changes, ...(rules.length ? { rules } : {}) };
}

const SLOT = 'craftsmanship';

/**
 * Craftsmanship, four modules in their own slot (the rulebook's table): a gun's is its reliability - Poor jams on
 * 91+ (Unreliable), Good only on 00 (Reliable), Best never (Blessed, which is exactly "never jams") - a blade's its
 * edge - Poor -10 to hit, Good +5, Best +10 and a point of damage. A GM's own craftsmanship module (slot
 * "craftsmanship") takes the place of these.
 */
export const CRAFTSMANSHIP: Record<'poor' | 'common' | 'good' | 'best', Module> = {
  poor: {
    name: 'Poor craftsmanship',
    slot: SLOT,
    changes: [
      { special: 'Unreliable', op: 'gain', for: 'ranged' },
      { field: 'attackMod', op: 'add', value: -10, for: 'melee' },
    ],
  },
  common: { name: 'Common craftsmanship', slot: SLOT, changes: [] },
  good: {
    name: 'Good craftsmanship',
    slot: SLOT,
    changes: [
      { special: 'Reliable', op: 'gain', for: 'ranged' },
      { field: 'attackMod', op: 'add', value: 5, for: 'melee' },
    ],
  },
  best: {
    name: 'Best craftsmanship',
    slot: SLOT,
    changes: [
      { special: 'Blessed', op: 'gain', for: 'ranged' },
      { field: 'attackMod', op: 'add', value: 10, for: 'melee' },
      { field: 'damageFlat', op: 'add', value: 1, for: 'melee' },
    ],
  },
};

/** How each number reads in a sentence. */
export const NUMBER_WORDS: Record<NumberField, string> = {
  penetration: 'Pen',
  range: 'range',
  clip: 'clip',
  semi: 'semi-auto',
  full: 'full-auto',
  attackMod: 'to hit',
  reach: 'reach',
  damageDice: 'damage d10s',
  damageFlat: 'flat damage',
};
/** "= 8", "+5", "-1", "×0.5": an addition shows its own sign. */
const OP_WORDS = {
  set: (v: number) => ` = ${v}`,
  add: (v: number) => (v < 0 ? ` ${v}` : ` +${v}`),
  multiply: (v: number) => ` ×${v}`,
} as const;
const FOR_WORDS = { ranged: ' (guns)', melee: ' (blades)' } as const;

/** One change in words: "Pen = 8", "gains Tearing (guns)", "Signature muffled". */
export function changeSentence(c: ModuleChange): string {
  const what =
    'field' in c
      ? `${NUMBER_WORDS[c.field]}${OP_WORDS[c.op](c.value)}`
      : 'special' in c
        ? `${c.op === 'gain' ? 'gains' : 'loses'} ${c.special}`
        : `Signature ${c.signature}`;
  return `${what}${c.for ? FOR_WORDS[c.for] : ''}`;
}

/** A module in words: its changes, then how many rules it brings. */
export function moduleSentence(m: Module): string {
  const parts = m.changes.map(changeSentence);
  if (m.rules?.length) parts.push(`${m.rules.length} conditional rule${m.rules.length === 1 ? '' : 's'}`);
  return parts.join(', ') || 'no change';
}

/**
 * A few modules to start from (invented, as the canvas's were): the GM's own live in the library. They show the
 * grammar - a slot, a fit, numbers, a special, the Signature - and are copied onto a weapon, never referenced.
 */
export const EXAMPLE_MODULES: readonly Module[] = [
  {
    name: 'Suppressor',
    slot: 'muzzle',
    fits: [[{ kind: 'attack', is: 'ranged' }]],
    changes: [{ field: 'range', op: 'multiply', value: 0.75 }, { signature: 'muffled' }],
  },
  {
    name: 'Red-dot sight',
    slot: 'sight',
    fits: [[{ kind: 'attack', is: 'ranged' }]],
    changes: [{ field: 'attackMod', op: 'add', value: 5 }],
  },
  {
    name: 'Extended magazine',
    slot: 'magazine',
    fits: [[{ kind: 'attack', is: 'ranged' }]],
    changes: [{ field: 'clip', op: 'multiply', value: 1.5 }],
  },
  {
    name: 'Monomolecular edge',
    slot: 'edge',
    fits: [[{ kind: 'attack', is: 'melee' }]],
    changes: [{ field: 'penetration', op: 'add', value: 2 }],
  },
];
